import React from "react";
import { rupiah } from "@/lib/format";
import { Logo } from "@/components/Logo";
import * as W from "@/lib/waktu";

// Addendum 5 (A5-C): A4 invoice from the server's data (snapshot of the nota).
// The page never recalculates money; the browser's "Simpan PDF" makes the file.
const BAYAR = { BELUM_BAYAR: "Belum bayar", DP: "Dibayar sebagian", LUNAS: "Lunas" };
const tgl = (iso) => (iso ? W.tanggalPanjang(iso) : "—");

export default function InvoiceA4({ d }) {
  const t = d.termin;
  const p = d.pembeli;
  return (
    <article data-testid="invoice-a4" className="invoice-a4 mx-auto bg-white text-slate-900 text-[13px] leading-snug">
      {/* Lines use the fixed dark #1E293B (paper document; the app theme maps slate-800 to a light line).
          TAMPILAN-SOQ: on screen the sheet is A4-wide with a 14 mm inner margin
          (same as the printed page); on a phone a smaller margin. On paper the
          page margin (@page, 14 mm) is the only margin — never doubled. */}
      <style>{`@page { size: A4; margin: 14mm; }
        .invoice-a4 { width: 210mm; max-width: 100%; padding: 14mm; box-sizing: border-box; }
        @media screen { .invoice-a4 { border-radius: 4px; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12), 0 8px 24px rgba(15, 23, 42, 0.06); } }
        @media screen and (max-width: 640px) { .invoice-a4 { padding: 20px 16px; } }
        @media print { .invoice-a4 { width: auto; padding: 0; box-shadow: none; border-radius: 0; } .invoice-geser { overflow: visible !important; } }`}</style>
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b-2 border-[#1E293B] pb-3">
        <div className="flex items-start gap-3">
          <Logo varian="hitam" className="h-10 sm:h-12 w-auto shrink-0" />
          <div>
            <div className="text-lg font-bold">{d.penjual.nama_usaha}</div>
            {d.penjual.alamat && <div className="whitespace-pre-line text-xs">{d.penjual.alamat}</div>}
            {d.penjual.kontak && <div className="text-xs">{d.penjual.kontak}</div>}
            {d.penjual.npwp && <div className="text-xs">NPWP {d.penjual.npwp}</div>}
          </div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-2xl font-bold tracking-wider">INVOICE</div>
          <div data-testid="invoice-nomor" className="font-mono">{d.nomor_invoice}</div>
          <div className="text-xs">Tanggal {tgl(d.dicetak_pada || new Date().toISOString())}</div>
          {t && <div data-testid="invoice-termin" className="mt-1 inline-block rounded border border-[#1E293B] px-2 py-0.5 text-xs font-semibold">Termin {t.ke || ""} — {t.persen}%</div>}
        </div>
      </header>
      {d.dibatalkan && <div className="my-3 rounded border-2 border-red-600 py-1 text-center font-bold text-red-600">NOTA DIBATALKAN</div>}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 py-3">
        <div>
          <div className="text-xs uppercase text-slate-500">Kepada</div>
          {p.nama_instansi && <div className="font-semibold">{p.nama_instansi}</div>}
          <div>{p.nama_instansi ? `u.p. ${p.pic_nama || p.nama}` : p.nama}</div>
          {p.alamat && <div className="whitespace-pre-line text-xs">{p.alamat}</div>}
          {p.npwp && <div className="text-xs">NPWP {p.npwp}</div>}
        </div>
        <div className="text-xs sm:text-right">
          <div>No. nota: <span className="font-mono">{d.nomor_invoice}</span></div>
          <div>Tanggal nota: {tgl(d.tanggal_nota)}</div>
          <div>Status: <b data-testid="invoice-status">{d.dibatalkan ? "Dibatalkan" : BAYAR[d.status_bayar]}</b></div>
        </div>
      </section>
      <div className="invoice-geser overflow-x-auto"><table className="w-full border-collapse text-xs">
        <thead><tr className="border-y border-[#1E293B] text-left"><th className="py-1 pr-2 w-6">No</th><th className="py-1 pr-2">Uraian</th><th className="py-1 pr-2">Rincian</th><th className="py-1 text-right">Jumlah</th></tr></thead>
        <tbody>
          {d.baris.map((b, i) => (
            <tr key={i} className="border-b border-slate-200 align-top">
              <td className="py-1 pr-2">{i + 1}</td>
              <td className="py-1 pr-2">{b.nama}{b.detail ? <div className="text-slate-500">{b.detail}</div> : null}</td>
              <td className="py-1 pr-2 font-mono">{b.hitung}{b.diskon > 0 ? ` − diskon ${rupiah(b.diskon)}` : ""}</td>
              <td className="py-1 text-right font-mono">{rupiah(b.jumlah)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <section className="ml-auto mt-2 w-full sm:w-1/2 space-y-0.5 text-sm">
        <Baris label="Subtotal" nilai={d.subtotal} />
        {d.diskon > 0 && <Baris label="Diskon" nilai={-d.diskon} />}
        {d.ppn && d.ppn.mode !== "INKLUSIF" && <Baris label="DPP" nilai={d.ppn.dpp} />}
        {d.ppn && <Baris testid="invoice-ppn" label={`PPN ${d.ppn.tarif}%${d.ppn.mode === "INKLUSIF" ? " (termasuk)" : ""}`} nilai={d.ppn.ppn} />}
        {!d.ppn && p.is_instansi && <Baris label="PPN" teks="—" />}
        <Baris testid="invoice-total" label="Grand total" nilai={d.total} tebal />
        <Baris label="Sudah dibayar" nilai={d.dibayar} />
        <Baris label="Sisa" nilai={d.sisa} />
        {t && <Baris testid="invoice-tagihan" label={`Tagihan termin ini (${t.persen}%)`} nilai={t.tagihan} tebal />}
      </section>
      <p className="mt-2 text-xs italic">Terbilang: <span data-testid="invoice-terbilang">{t ? (t.terbilang_tagihan || "Lunas / tidak ada tagihan termin ini") : d.terbilang_total}</span></p>
      {d.rekening.length > 0 && (
        <section className="mt-3 text-xs">
          <div className="font-semibold">Pembayaran transfer ke:</div>
          {d.rekening.map((r, i) => <div key={i}>{r.bank} {r.nomor} a.n. {r.atas_nama}</div>)}
        </section>
      )}
      <section className="mt-6 flex justify-end">
        <div className="w-56 text-center text-xs">
          <div>Hormat kami,</div>
          <div className="h-20" />
          <div className="border-t border-[#1E293B] pt-1 font-semibold">{d.penanda_tangan.nama || " "}</div>
          <div>{d.penanda_tangan.jabatan}</div>
        </div>
      </section>
      {d.ppn && <p className="mt-4 text-[11px] text-slate-500">{d.catatan_pajak}</p>}
    </article>
  );
}

function Baris({ label, nilai, teks, tebal, testid }) {
  return (
    <div className={`flex justify-between gap-3 ${tebal ? "border-t border-[#1E293B] pt-0.5 font-bold" : ""}`}>
      <span className="whitespace-nowrap">{label}</span><span data-testid={testid} className="font-mono">{teks ?? rupiah(nilai)}</span>
    </div>
  );
}
